import React, { useState, useEffect, useCallback } from 'react';
import SearchableDropdown from './SearchableDropdown';
import KpiCard from './KpiCard';
import Card from './Card';
import CardHeader from './CardHeader';
import CompareSchoolsFullWidthHORIZONTAL from './CompareSchoolsFullWidth';
import TrendTotal from './TrendTotal';
import TrendGender from './TrendGender';
import CoverageMiniCard from './CoverageMiniCard';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { useKpiMetadata } from '../services/useKpiMetadata';

interface KpiSectionProps {
  kommunId: string | null;
  skolaId: string | null;
  year: number;
  kpiId: string;
  onKpiChange: (kpiId: string) => void;
  compareScope?: 'municipality' | 'national';
}

const KpiSection: React.FC<KpiSectionProps> = ({
  kommunId,
  skolaId,
  year,
  kpiId,
  onKpiChange,
  compareScope = 'municipality'
}) => {
  const [kpiDataAvailability, setKpiDataAvailability] = useState<Record<string, boolean>>({});
  const [trendDomain, setTrendDomain] = useState<[number, number] | null>(null);
  const [yearTicks, setYearTicks] = useState<number[] | null>(null);
  const [kpiValues, setKpiValues] = useState<{
    schoolValue: number | null;
    muniValue: number | null;
    delta: number | null;
    percentile: number | null;
  }>({
    schoolValue: null,
    muniValue: null,
    delta: null,
    percentile: null
  });
  const [dataLoading, setDataLoading] = useState(false);
  const { query } = useDuckDB();
  const { kpiMetadata, loading: kpiLoading, error: kpiError, getKpiInfo, getKpiOptions } = useKpiMetadata();

  // Check data availability for all KPIs
  const checkKpiDataAvailability = useCallback(async () => {
    if (!skolaId || !kommunId || kpiMetadata.length === 0) return;
    
    const availability: Record<string, boolean> = {};
    
    for (const kpi of kpiMetadata) {
      try {
        const sql = `
          SELECT COUNT(*) as count
          FROM gold_kpi_trend
          WHERE entity_type='org_unit' AND entity_id='${skolaId}' AND kpi_id='${kpi.id}'
            AND gender = 'T' AND year <= ${year} AND value IS NOT NULL
        `;
        const result = await query(sql);
        availability[kpi.id] = (result[0] as any)?.count > 0;
      } catch (error) {
        console.error(`Error checking data for indikator ${kpi.id}:`, error);
        availability[kpi.id] = false;
      }
    }
    
    setKpiDataAvailability(availability);
  }, [query, skolaId, kommunId, kpiMetadata, year]);

  // Get current KPI info
  const currentKpi = getKpiInfo(kpiId);

  // Fetch actual KPI values for the selected year
  const fetchKpiValues = useCallback(async () => {
    if (!skolaId || !kommunId || !kpiId) return;
    
    setDataLoading(true);
    try {
      // Get school value for the selected year
      const schoolSql = `
        SELECT value
        FROM gold_kpi_trend
        WHERE entity_type='org_unit' AND entity_id='${skolaId}' AND kpi_id='${kpiId}'
          AND gender = 'T' AND year = ${year} AND value IS NOT NULL
        LIMIT 1
      `;
      
      // Get reference value for the selected year (municipality or national)
      const muniSql = (
        `${(
          compareScope === 'municipality'
            ? `SELECT value_t AS ref_value FROM gold_kpi_muni_year WHERE municipality_id='${kommunId}' AND kpi_id='${kpiId}' AND year = ${year} LIMIT 1`
            : `SELECT AVG(value) AS ref_value FROM gold_kpi_trend WHERE entity_type='org_unit' AND kpi_id='${kpiId}' AND gender='T' AND year = ${year} AND value IS NOT NULL`
        )}`
      );
      
      // Get percentile ranking (municipal or national)
      const betterIsHigher = currentKpi?.betterIsHigher ?? true;
      const percentileSql = `
        WITH school_value AS (
          SELECT value
          FROM gold_kpi_trend
          WHERE entity_type='org_unit' AND entity_id='${skolaId}' AND kpi_id='${kpiId}'
            AND gender = 'T' AND year = ${year} AND value IS NOT NULL
          LIMIT 1
        ),
        all_schools AS (
          SELECT value
          FROM gold_kpi_trend
          WHERE entity_type='org_unit' AND kpi_id='${kpiId}' AND gender = 'T'
            ${compareScope === 'municipality' ? `AND municipality_id='${kommunId}'` : ''} AND year = ${year} AND value IS NOT NULL
        )
        SELECT 
          sv.value as school_value,
          COUNT(*) as total_schools,
          CASE WHEN ${betterIsHigher}
            THEN 100.0 * COUNT(CASE WHEN asv.value < sv.value THEN 1 END) / COUNT(*)
            ELSE 100.0 * COUNT(CASE WHEN asv.value > sv.value THEN 1 END) / COUNT(*)
          END AS percentile
        FROM school_value sv
        CROSS JOIN all_schools asv
        GROUP BY sv.value
      `;
      
      const [schoolResult, muniResult, percentileResult] = await Promise.all([
        query(schoolSql),
        query(muniSql),
        query(percentileSql)
      ]);
      
      const schoolValue = (schoolResult[0] as any)?.value || null;
      const muniValue = (muniResult[0] as any)?.ref_value || (muniResult[0] as any)?.value_t || null;
      const delta = schoolValue && muniValue ? Number(schoolValue) - Number(muniValue) : null;
      
      let percentile = null;
      if (percentileResult[0]) {
        percentile = Math.round(Number((percentileResult[0] as any).percentile));
      }
      
      setKpiValues({
        schoolValue: schoolValue ? Number(schoolValue) : null,
        muniValue: muniValue ? Number(muniValue) : null,
        delta,
        percentile
      });

      // Compute shared domain and ticks for small multiples
      try {
        const domainSql = `
          WITH school_vals AS (
            SELECT year, value AS v
            FROM gold_kpi_trend
            WHERE entity_type='org_unit' AND entity_id='${skolaId}' AND kpi_id='${kpiId}'
              AND gender IN ('T','K','M') AND year <= ${year} AND value IS NOT NULL
          ),
          muni_vals AS (
            SELECT year, value_t AS v
            FROM gold_kpi_muni_year
            WHERE municipality_id='${kommunId}' AND kpi_id='${kpiId}' AND year <= ${year} AND value_t IS NOT NULL
          ),
          all_vals AS (
            SELECT * FROM school_vals UNION ALL SELECT * FROM muni_vals
          )
          SELECT MIN(v) AS min_v, MAX(v) AS max_v, MIN(year) AS min_y, MAX(year) AS max_y FROM all_vals
        `;
        const dom = await query(domainSql);
        const minV = Number(dom[0]?.min_v ?? 0);
        const maxV = Number(dom[0]?.max_v ?? 100);
        const minY = Number(dom[0]?.min_y ?? year - 5);
        const maxY = Number(dom[0]?.max_y ?? year);
        const pad = Math.max(1, Math.round((maxV - minV) * 0.06));
        const yMin = Math.floor((minV - pad) / 5) * 5;
        const yMax = Math.ceil((maxV + pad) / 5) * 5;
        setTrendDomain([yMin, yMax]);
        const ticks: number[] = [];
        for (let y = minY; y <= maxY; y++) ticks.push(y);
        setYearTicks(ticks);
      } catch {}
      
    } catch (error) {
      console.error('Error fetching indikator values:', error);
      setKpiValues({
        schoolValue: null,
        muniValue: null,
        delta: null,
        percentile: null
      });
    } finally {
      setDataLoading(false);
    }
  }, [query, skolaId, kommunId, kpiId, year, compareScope, currentKpi?.betterIsHigher]);

  // Check data availability when school changes
  useEffect(() => {
    checkKpiDataAvailability();
    fetchKpiValues();
  }, [checkKpiDataAvailability, fetchKpiValues]);



  // Helper function to get the appropriate title for the main KPI card
  const getKpiTitle = (kpiId: string) => {
    const kpiInfo = getKpiInfo(kpiId);
    return kpiInfo ? `${kpiInfo.name.split(',')[0]} ${year}` : `KPI ${kpiId} ${year}`; // Get the first part before comma
  };

  // Helper function to get KPI description for help tooltips
  const getKpiDescription = (kpiId: string) => {
    switch (kpiId) {
      case 'N15504': // Meritvärde
        return 'Meritvärde visar genomsnittligt betygspoäng för elever i årskurs 9. Skalan går från 0 till 340 poäng. Högre poäng betyder bättre betyg. Källa: Skolverket (Siris).';
      case 'N15418': // Betygskriterier
        return 'Visar andel elever som uppnått betygskriterier i alla ämnen i årskurs 9. Procenttalet visar hur många av eleverna som nått målen i alla ämnen. Källa: Skolverket (Siris).';
      case 'N15436': // Behörighet yrkesprogram
        return 'Antal elever i årskurs 9 som är behöriga till ett yrkesprogram dividerat med antal elever som fått eller skulle ha fått betyg i minst ett ämne enligt det mål- och kunskapsrelaterade betygssystemet i årskurs 9. För att ha gymnasiebehörighet till ett yrkesprogram krävs godkända betyg i svenska eller svenska som andraspråk, engelska och matematik och i minst fem andra ämnen från grundskolan. Källa: Skolverket (Siris).';
      case 'N15482': // Engelska E
        return 'Visar andel elever som har minst betyg E i engelska i årskurs 6. Procenttalet visar hur många som uppnått grundläggande kunskaper i engelska. Källa: Skolverket (Siris).';
      default:
        return 'Detta nyckeltal visar resultat för den valda skolan jämfört med kommunens genomsnitt. Källa: Skolverket (Siris).';
    }
  };

  // Helper function to get calculation explanations
  const getCalculationExplanation = (type: string) => {
    switch (type) {
      case 'delta':
        return 'Skillnaden mellan skolans värde och kommunens genomsnitt för samma år. Beräknas som: Skolans värde - Kommunens genomsnitt. Positivt värde betyder att skolan presterar bättre än genomsnittet, negativt värde betyder sämre.';
      case 'percentil':
        return 'Visar skolans placering bland alla skolor i kommunen. Beräknas som: (Antal skolor med lägre värde / Totalt antal skolor) × 100. 50 betyder att skolan är mitt i genomsnittet, 90 betyder att den är bland de 10% bästa skolorna.';
      case 'comparison':
        return 'Jämför skolan med andra skolor i samma kommun. Skolorna sorteras från högst till lägst värde. Din valda skola markeras med färg.';
      case 'trend':
        return 'Visar utvecklingen över tid. Linjerna visar hur skolan och kommunen har utvecklats under de senaste åren.';
      default:
        return '';
    }
  };

  // Show loading state if KPI metadata is still loading
  if (kpiLoading) {
    return (
      <section
        aria-labelledby="kpi-title"
        className="rounded-xl bg-emerald-50/80 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800 overflow-visible"
      >
        <div className="p-4 text-center">
          <div className="text-sm text-muted-foreground">Laddar KPI-data...</div>
        </div>
      </section>
    );
  }

  // Show error state if KPI metadata failed to load
  if (kpiError) {
    return (
      <section
        aria-labelledby="kpi-title"
        className="rounded-xl bg-red-50/80 dark:bg-red-900/20 ring-1 ring-red-200 dark:ring-red-800 overflow-visible"
      >
        <div className="p-4 text-center">
          <div className="text-sm text-red-600 dark:text-red-400">
            Fel vid laddning av KPI-data: {kpiError}
          </div>
        </div>
      </section>
    );
  }

  // Show message if no KPI metadata is available
  if (kpiMetadata.length === 0) {
    return (
      <section
        aria-labelledby="kpi-title"
        className="rounded-xl bg-yellow-50/80 dark:bg-yellow-900/20 ring-1 ring-yellow-200 dark:ring-yellow-800 overflow-visible"
      >
        <div className="p-4 text-center">
          <div className="text-sm text-yellow-600 dark:text-yellow-400">
            Inga KPI:er konfigurerade. Kontrollera konfigurationsfilen.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="kpi-title"
      className="rounded-xl bg-emerald-50/80 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800 overflow-visible relative"
    >
      {/* Loading overlay for data transitions */}
      {dataLoading && (
        <div className="absolute inset-0 bg-emerald-50/80 dark:bg-emerald-900/20 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">Laddar data...</p>
          </div>
        </div>
      )}
      <header className="grid gap-3 md:gap-4 lg:grid-cols-[1fr_280px] items-start p-4 border-b border-emerald-200 dark:border-emerald-800 overflow-visible">
        {/* Vänster: titel + beskrivning */}
        <div className="min-w-0">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-1 min-w-0">
              {/* Smart title styling with truncation */}
              <h2 
                id="kpi-title" 
                className="text-lg font-semibold leading-tight mb-2"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.3'
                }}
                title={currentKpi?.name || 'Okänd KPI'}
              >
                {currentKpi?.name || 'Okänd KPI'}
              </h2>
              
              {/* Metadata chips */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs rounded-full bg-emerald-200/80 text-emerald-900 dark:bg-emerald-800/60 dark:text-emerald-200 px-2 py-1">
                  {compareScope === 'municipality' ? 'Kommun & Skola' : 'Skola & Riket'}
                </span>
                {currentKpi?.operatingArea && (
                  <span className="text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1">
                    {currentKpi.operatingArea}
                  </span>
                )}
                {currentKpi?.perspective && (
                  <span className="text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-1">
                    {currentKpi.perspective}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Enhanced description with better typography */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {currentKpi?.description}
            </p>
            
            {/* Source footer */}
            <p className="text-xs text-muted-foreground/70 mt-3 pt-2 border-t border-muted-foreground/20">
              Källa: Skolverket (Siris)
            </p>
          </div>
        </div>

        {/* Höger: KPI-väljare + kompakt täckningskort */}
        <div className="space-y-3 overflow-visible">
          <SearchableDropdown
            options={getKpiOptions().map(k => ({
              id: k.id,
              name: kpiDataAvailability[k.id] === false ? `⚠️ ${k.name}` : k.name
            }))}
            value={kpiId}
            onChange={onKpiChange}
            placeholder="Välj indikator…"
            width="220px"
          />
          <CoverageMiniCard
            kpiId={kpiId}
            currentYear={year}
            muni={kommunId || ''}
            compareScope={compareScope}
          />
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* KPI cards - enhanced layout */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Main KPI Value Card */}
          <div className="lg:col-span-1">
            <KpiCard 
              title={getKpiTitle(kpiId)} 
              value={kpiValues.schoolValue ? kpiValues.schoolValue.toFixed(1) : 'N/A'} 
              helper={currentKpi?.helperText || ''}
              helpText={compareScope === 'municipality' ? getKpiDescription(kpiId) : `${getKpiDescription(kpiId)} (Jämförelse mot riket)`}
            />
          </div>
          
          {/* Delta Card */}
          <div className="lg:col-span-1">
            <KpiCard 
              title={compareScope === 'municipality' ? 'Δ mot kommun' : 'Δ mot riket'} 
              value={kpiValues.delta !== null ? (kpiValues.delta > 0 ? `+${kpiValues.delta.toFixed(1)}` : kpiValues.delta.toFixed(1)) : 'N/A'} 
              tone={kpiValues.delta !== null ? (kpiValues.delta > 0 ? 'positive' : kpiValues.delta < 0 ? 'negative' : 'neutral') : 'neutral'}
              helpText={compareScope === 'municipality' ? getCalculationExplanation('delta') : 'Skillnaden mellan skolans värde och rikets genomsnitt för samma år. Beräknas som: Skolans värde - Riksgenomsnittet.'}
              helpTitle="Hur räknas skillnaden ut?"
            />
          </div>
          
          {/* Percentile Card */}
          <div className="lg:col-span-1">
            <KpiCard 
              title="Percentil" 
              value={kpiValues.percentile !== null ? kpiValues.percentile.toString() : 'N/A'}
              helpText={compareScope === 'municipality' ? getCalculationExplanation('percentil') : 'Visar skolans placering bland alla skolor i riket. Beräknas som: (Antal skolor i riket med lägre värde / Totalt antal skolor i riket) × 100.'}
              helpTitle="Vad betyder percentil?"
            />
          </div>
        </div>

        {/* Full width: Compare schools */}
        <Card className="col-span-12">
          <CardHeader
            title={`Jämförelse med andra skolor - ${compareScope === 'municipality' ? `Kommunen ${year}` : `Riket ${year}`}`}
            helpKind="chart"
            shortText="Staplar visar värden per skola. Jämför Top/Bottom 10 och vald skola."
            longText={getCalculationExplanation('comparison')}
            helpTitle="Hur fungerar jämförelsen?"
            showWarning={kpiDataAvailability[kpiId] === false}
          />
          <CompareSchoolsFullWidthHORIZONTAL 
            kommunId={kommunId || ''}
            kpiId={kpiId}
            skolaId={skolaId}
            selectedYear={year}
            compareScope={compareScope}
          />
        </Card>

        {/* Trend cluster - enhanced headers */}
        <div className="grid gap-4 md:gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader
              title="📊 Totalt - Alla elever"
              helpKind="chart"
              shortText="Utveckling över tid för alla elever."
              longText={getCalculationExplanation('trend')}
              helpTitle="Vad visar trendgrafen?"
              showWarning={kpiDataAvailability[kpiId] === false}
            />
            <TrendTotal 
              kommunId={kommunId || ''}
              skolaId={skolaId}
              kpiId={kpiId}
              selectedYear={year}
              yDomain={trendDomain || undefined}
              yearTicks={yearTicks || undefined}
              compareScope={compareScope}
            />
          </Card>
          <Card>
            <CardHeader
              title="👩 Flickor - Könsspecifik utveckling"
              helpKind="chart"
              shortText="Utveckling för flickor över tid."
              longText="Visar utvecklingen för flickor över tid. Jämför med kommunens genomsnitt för flickor."
              helpTitle="Vad visar flickornas trend?"
              showWarning={kpiDataAvailability[kpiId] === false}
            />
            <TrendGender 
              kommunId={kommunId || ''}
              skolaId={skolaId}
              kpiId={kpiId}
              gender="K"
              title="Flickor"
              selectedYear={year}
              yDomain={trendDomain || undefined}
              yearTicks={yearTicks || undefined}
              compareScope={compareScope}
            />
          </Card>
          <Card>
            <CardHeader
              title="👦 Pojkar - Könsspecifik utveckling"
              helpKind="chart"
              shortText="Utveckling för pojkar över tid."
              longText="Visar utvecklingen för pojkar över tid. Jämför med kommunens genomsnitt för pojkar."
              helpTitle="Vad visar pojkarnas trend?"
              showWarning={kpiDataAvailability[kpiId] === false}
            />
            <TrendGender 
              kommunId={kommunId || ''}
              skolaId={skolaId}
              kpiId={kpiId}
              gender="M"
              title="Pojkar"
              selectedYear={year}
              yDomain={trendDomain || undefined}
              yearTicks={yearTicks || undefined}
              compareScope={compareScope}
            />
          </Card>
        </div>

        {/* Full width: SALSA strip - Hidden for now */}
        {/* <Card title="Avvikelse från modellberäknat värde (SALSA)" className="col-span-12">
          <SalsaStrip />
        </Card> */}
      </div>
    </section>
  );
};

export default KpiSection;
